import './FenceBody.scss';

import React, {useEffect, useMemo} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {Link} from 'react-router-dom';
import {equals} from 'ramda';
import {is, Seq} from 'immutable';

import {Col} from '../../../elements/flex';
import ShowVisTagsItems from "./ShowVisTagsItems";

import {IProps} from "./FenceBody.interfaces";
import {FenceGroup, FenceType, ItemType, UserAuth, iFenceDetails, iFullStoreState} from '../../../../shared/interfaces';
import {ALL_DEVICES_TAG, isMobile} from '../../../../shared/helpers';
import C, {ACL, UserCan} from '../../../../shared/constants';
import {keyGen} from '../../../../shared/firebase';

import * as fenceDb from '../../../../shared/db/fences-db';
import * as fenceSelectors from '../../../../shared/db/fences-selectors';

import {Actions, useVisRegions} from '../../../../stores/reducers/map-region-reducers';
import {Actions as PathActions} from '../../../../stores/reducers/map-path-reducers';
import {Actions as FenceActions} from '../../../../stores/reducers/fences-reducers';

import {useIsReadOnly} from "../../../../hooks/useIsReadOnly";
import {useRedux} from '../../../../states/redux-state';

import FenceSearchBar from './FenceSearchBar';
import FencePagination from './FencePagination';
import FencesList from './FencesList';
import { useFenceManagement } from '../../../../hooks/useFenceManagement';
import FenceButton from './FenceButton';

function FenceBody(props: IProps): JSX.Element {
    const {itemId, itemType} = props;

    const dispatch = useDispatch();

    const isGlobalPage = itemType === ItemType.device && !itemId;

    const allDevicesTagFences = useRedux((s) => s.general.fences?.tag?.[ALL_DEVICES_TAG]);

    const fullState = useSelector((s: iFullStoreState) => s);

    const devicesIds = useSelector<iFullStoreState, Seq.Indexed<string>>(
        (s) => s.devicesData.devicesDetails.keySeq(),
        is
    ).toArray();

    const fences = useSelector<iFullStoreState, Array<iFenceDetails>>(
        (state) =>
            fenceSelectors.fenceOrPathSelector(
                itemType,
                itemId,
                FenceGroup.FENCE
            )(state, {itemType, itemId}),
        equals
    );

    const paths = useSelector<iFullStoreState, Array<iFenceDetails>>(
        (state) =>
            fenceSelectors.fenceOrPathSelector(
                itemType,
                itemId,
                FenceGroup.PATH
            )(state, {itemType, itemId}),
        equals
    );

    const visibleRegions = useSelector<iFullStoreState, Array<string>>(
        (state) => Object.keys(state.regions.vis || {}),
        equals
    );

    const visiblePaths = useSelector<iFullStoreState, Array<string>>(
        (state) => Object.keys(state.paths.vis || {}),
        equals
    );

    const authUser = useSelector<iFullStoreState, UserAuth>((s) => s.auth.user, (l, r) => equals(l, r));

    const hasShowedTags = useSelector<iFullStoreState, boolean>(
        (s) => !!s.tagsDevicesMap.showedTagsIds.size
    );

    const allFences = allDevicesTagFences ? Object.values(allDevicesTagFences).concat(fences) : fences;

    const allDeviceFences = useMemo(() => {
        if (!devicesIds.length) return [];

        const deviceFences = devicesIds.flatMap((id) =>
            fenceSelectors.fenceOrPathSelector(
                ItemType.device,
                id,
                FenceGroup.FENCE
            )(fullState, {itemType: ItemType.device, itemId: id})
        );

        const tagFences = Object.values(fullState.general.fences?.tag || {}).flatMap(
            (tagFencesObj) => Object.values(tagFencesObj)
        );

        const uniqueMap = new Map<string, iFenceDetails>();

        // Merge fences
        [...deviceFences, ...tagFences].forEach((f) => uniqueMap.set(f.id, f));

        return Array.from(uniqueMap.values());
    }, [devicesIds, fullState.general.fences]);

    const mapOnly = (!itemId || !['device', 'person'].includes(itemType)) && !hasShowedTags;
    const isReadOnly = useIsReadOnly(itemId, itemType as ItemType);
    const hasPermissionToEditFences = ACL.check(UserCan.EDIT_FENCES, authUser.acl.can);
    const canEditFence = !isReadOnly && hasPermissionToEditFences;
    const centered = canEditFence ? '' : 'ButtonsBlockCentered';

    useEffect(() => {
        dispatch(Actions.UNSTASH_REGIONS());
    }, []);

    // Fences to render based on item type
    const fencesToRender = useMemo(() => {
        // Global page fences
        if (isGlobalPage) {
            return allDeviceFences;
        }

        // Fences for specific item
        return fences;
    }, [itemType, itemId, allDeviceFences, fences]);

    const toggleFencePath = async (id: string, on: boolean) => {
        const allFencesCombined = [...allDeviceFences, ...allFences, ...paths];
        const fence = allFencesCombined.find((x) => x.id === id);

        if (!fence) {
            console.warn(`Fence with id=${id} not found`);
            return;
        }

        if (on && (visiblePaths.includes(id) || visibleRegions.includes(id))) {
            return;
        }

        if (!on) {
            dispatch(
                fence.fenceType === FenceType.PATH
                    ? PathActions.REMOVE_PATH(id)
                    : Actions.REMOVE_REGION(id)
            );
        } else {
            dispatch(
                fence.fenceType === FenceType.PATH
                    ? PathActions.PLACE_FENCE(fence, await fenceDb.getPath(id))
                    : Actions.PLACE_FENCE(fence, false)
            );
        }
    };

    const toggleAll = (region: boolean, on: boolean) => {
        const toToggle = region ? fences : paths;
        toToggle.forEach((fence) => toggleFencePath(fence.id, on));
    };

    const {
        currentPage,
        filter,
        filteredItems: filteredFences,
        paginatedItems: paginatedFences,
        totalPages,
        handleSearchChange,
        handlePageChange
    } = useFenceManagement(fencesToRender);

    if (mapOnly && isMobile) {
        return <Col justify="center" align="center" className='FenceBody-MobileNotify'>
            <h4>Please select device person or tag to create a fence</h4>
        </Col>;
    }

    return itemType === ItemType.device ? (
        <div className={`FenceBody ${isMobile ? 'FenceBody-Mobile' : ''}`}>
            {/* Content */}
            <div className="FenceBody-Content">
                {/* Fences Search */}
                <FenceSearchBar
                    filter={filter}
                    onFilterChange={handleSearchChange}
                    resultsCount={filteredFences.length}
                />

                {/* Fences List */}
                <FencesList
                    fences={paginatedFences}
                    itemType={itemType}
                    visibleRegions={visibleRegions}
                    onToggleFence={toggleFencePath}
                    isGlobalPage={isGlobalPage}
                />

                {!isGlobalPage && (
                    <Link
                        className="FenceBody-SimpleLink"
                        title="Create New Fence"
                        to={`/${ItemType.device}/${itemId}/${FenceGroup.FENCE}/${keyGen()}/edit`}
                        onClick={() => dispatch(FenceActions.SET_BACKUP_STATUS(true))}
                    >
                        + Add new
                    </Link>
                )}

            </div>

            {/* Pagination */}
            <FencePagination
                currentPage={currentPage}
                totalPages={totalPages}
                totalItems={filteredFences.length}
                onPageChange={handlePageChange}
            />

            {/* Buttons */}
            {!isGlobalPage && (
                <div className={`ButtonsBlock ${centered}`}>
                    {/* Toggle All */}
                    <FenceButton
                        type="selectAll"
                        onClick={() => toggleAll(true, fences.length !== visibleRegions.length)}
                        isSelected={fences.length === visibleRegions.length}
                    />

                    {/* Add Fence */}
                    {canEditFence && (
                        <FenceButton
                            type="addFence"
                            linkTo={`/${ItemType.device}/${itemId}/${FenceGroup.FENCE}/${keyGen()}/edit`}
                            onClick={() => dispatch(FenceActions.SET_BACKUP_STATUS(true))}
                        />
                    )}
                </div>
            )}
        </div>
    ) : (
        <ShowVisTagsItems itemId={itemId} toggleAll={toggleAll}/>
    );
}

export default FenceBody;